// DESCRIPTION: Compact mini-calendar grid for mobile month view — shows date numbers with
// event indicator dots and allows tapping a date to scroll the agenda below.

import React from 'react';

const SHORT_DAYS_SUN = [ 'S', 'M', 'T', 'W', 'T', 'F', 'S' ];
const SHORT_DAYS_MON = [ 'M', 'T', 'W', 'T', 'F', 'S', 'S' ];

/**
 * Convert a Date to YYYY-MM-DD.
 *
 * @param {Date} d
 *
 * @return {string}
 */
function toISO( d ) {
	return d.toISOString().slice( 0, 10 );
}

/**
 * MiniCalendar
 *
 * @param {Object}   props
 * @param {Date}     props.anchor          First day of the displayed month.
 * @param {number}   props.firstDay        0 = Sunday, 1 = Monday.
 * @param {string}   props.dateFrom        ISO date string of first visible grid cell.
 * @param {Set}      props.datesWithEvents Set of ISO date strings that have events.
 * @param {string}   props.selectedDate    Currently selected/highlighted date (ISO string).
 * @param {Function} props.onDateSelect    Called with ISO date string when user taps a date.
 */
export default function MiniCalendar( {
	anchor,
	firstDay,
	dateFrom,
	datesWithEvents,
	selectedDate,
	onDateSelect,
} ) {
	const dayLabels = firstDay === 1 ? SHORT_DAYS_MON : SHORT_DAYS_SUN;

	// Build 42 cells.
	const gridStart = new Date( dateFrom );
	const cells = Array.from( { length: 42 }, ( _, i ) => {
		const d = new Date( gridStart );
		d.setDate( d.getDate() + i );
		return d;
	} );

	const todayStr = toISO( new Date() );
	const monthYear = `${ anchor.getFullYear() }-${ String(
		anchor.getMonth() + 1
	).padStart( 2, '0' ) }`;

	return (
		<div className="nb-mini-calendar">
			{ /* Day-of-week header */ }
			<div className="nb-mini-calendar__header">
				{ dayLabels.map( ( label, i ) => (
					<div key={ i } className="nb-mini-calendar__day-label">
						{ label }
					</div>
				) ) }
			</div>

			{ /* Date grid */ }
			<div className="nb-mini-calendar__grid">
				{ cells.map( ( date ) => {
					const iso = toISO( date );
					const isToday = iso === todayStr;
					const isOutside = ! iso.startsWith( monthYear );
					const hasEvents = datesWithEvents.has( iso );
					const isSelected = iso === selectedDate;

					const cellClasses = [
						'nb-mini-calendar__cell',
						isOutside ? 'nb-mini-calendar__cell--outside' : '',
						isToday ? 'nb-mini-calendar__cell--today' : '',
						isSelected ? 'nb-mini-calendar__cell--selected' : '',
					]
						.filter( Boolean )
						.join( ' ' );

					return (
						<button
							key={ iso }
							className={ cellClasses }
							onClick={ () => onDateSelect( iso ) }
							aria-label={ iso }
							aria-current={ isToday ? 'date' : undefined }
						>
							<span className="nb-mini-calendar__date">
								{ date.getDate() }
							</span>
							{ hasEvents && (
								<span
									className="nb-mini-calendar__dot"
									aria-hidden="true"
								/>
							) }
						</button>
					);
				} ) }
			</div>
		</div>
	);
}
